import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { BarChart3, TrendingUp, TrendingDown } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ProjectFinancialTrend({ companyId, projectId, formatCurrency }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!companyId || !projectId) return;
      setLoading(true);
      try {
        const res = await axios.get(
          `${API}/companies/${companyId}/construction/projects/${projectId}/financial-trend?months=6`
        );
        if (!cancelled) setData(res.data);
      } catch (e) {
        console.error("Erro ao carregar tendência da obra:", e);
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [companyId, projectId]);

  if (loading) {
    return (
      <Card data-testid="project-trend-loading">
        <CardContent className="py-6 text-center text-gray-500">Carregando tendência...</CardContent>
      </Card>
    );
  }

  if (!data || !data.trend) return null;

  const totalRecebido = data.trend.reduce((s, t) => s + (t.recebido || 0), 0);
  const totalGastoRealizado = data.trend.reduce((s, t) => s + (t.gasto_realizado || 0), 0);
  const totalGastoPrevisto = data.trend.reduce((s, t) => s + (t.gasto_previsto || 0), 0);
  const totalGasto = totalGastoRealizado + totalGastoPrevisto;
  const saldo = totalRecebido - totalGasto;
  const hasData = data.trend.some(t => (t.recebido + (t.gasto_realizado || 0) + (t.gasto_previsto || 0)) > 0);

  return (
    <Card data-testid="project-trend-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-black text-lg flex items-center">
          <BarChart3 className="h-5 w-5 mr-2 text-gray-700" />
          Recebido x Gasto — Últimos 6 meses
        </CardTitle>
        <p className="text-xs text-gray-500">
          Recebido: parcelas pagas · Gasto Realizado: medições concluídas · Gasto Previsto: medições pendentes
        </p>
      </CardHeader>
      <CardContent>
        {/* Resumo */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-green-50 p-3 rounded" data-testid="project-trend-recebido">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-600" />
              <span className="text-xs font-medium text-gray-600">Recebido (6m)</span>
            </div>
            <p className="text-lg font-bold text-green-700 mt-1">{formatCurrency(totalRecebido)}</p>
          </div>
          <div className="bg-red-50 p-3 rounded" data-testid="project-trend-gasto-realizado">
            <div className="flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-red-600" />
              <span className="text-xs font-medium text-gray-600">Gasto Realizado</span>
            </div>
            <p className="text-lg font-bold text-red-700 mt-1">{formatCurrency(totalGastoRealizado)}</p>
          </div>
          <div className="bg-orange-50 p-3 rounded" data-testid="project-trend-gasto-previsto">
            <div className="flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-orange-500" />
              <span className="text-xs font-medium text-gray-600">Gasto Previsto</span>
            </div>
            <p className="text-lg font-bold text-orange-600 mt-1">{formatCurrency(totalGastoPrevisto)}</p>
          </div>
          <div className={`p-3 rounded ${saldo >= 0 ? 'bg-blue-50' : 'bg-gray-100'}`} data-testid="project-trend-saldo">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-600">Saldo (6m)</span>
            </div>
            <p className={`text-lg font-bold mt-1 ${saldo >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
              {formatCurrency(saldo)}
            </p>
          </div>
        </div>

        {/* Gráfico */}
        {!hasData ? (
          <p className="text-center text-gray-500 py-8">Sem movimentação financeira nos últimos 6 meses</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.trend} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => v >= 1000 ? `R$ ${(v / 1000).toFixed(0)}k` : `R$ ${v}`}
              />
              <Tooltip
                formatter={(value) => formatCurrency(value)}
                labelStyle={{ color: '#000', fontWeight: 'bold' }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="recebido" name="Recebido" fill="#10b981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="gasto_realizado" name="Gasto Realizado" fill="#ef4444" radius={[4, 4, 0, 0]} />
              <Bar dataKey="gasto_previsto" name="Gasto Previsto" fill="#f97316" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

export default ProjectFinancialTrend;
